import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Cpu, Zap, Users } from 'lucide-react';
import './AboutPreview.css';

const focusAreas = [
  { icon: Code, label: 'Development', color: '#0D6EFD' },
  { icon: Cpu, label: 'AI / ML', color: '#00E5FF' },
  { icon: Zap, label: 'Innovation', color: '#A020F0' },
  { icon: Users, label: 'Community', color: '#0D6EFD' },
];

const achievements = [
  {
    title: 'Beyond Campus Visits',
    description: 'Extending knowledge and opportunities outside the classroom.',
  },
  {
    title: 'Event Sphere',
    description: 'A vibrant space for fests, competitions, and collaborations.',
  },
  {
    title: 'Innovative Workshops',
    description: 'Hands-on learning with the latest AI & tech tools.',
  },
];

/* Tripled for seamless CSS marquee loop */
const marqueeItems = [...achievements, ...achievements, ...achievements];

const AboutPreview = React.memo(({ onPageChange }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  /* Desktop: auto-rotate active card highlight */
  useEffect(() => {
    const id = setInterval(
      () => setActiveIdx(prev => (prev + 1) % achievements.length),
      4000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section className="about-section" id="about" aria-label="About AIEC IITM">
      <div className="about-bg-glow" aria-hidden="true" />

      <div className="about-wrapper">

        {/* ══ Heading + Description ══ */}
        <motion.div
          className="about-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <h2 className="about-title">
            About <span className="about-title-accent">AIEC IITM</span>
          </h2>
          <p className="about-desc">
            The AIEC of IITM Janakpuri is a hub for innovation and learning in
            AI, ML, and emerging technologies. Through workshops, projects,
            competitions, and industry collaborations, it equips students with
            hands-on skills to become problem-solvers and innovators driving
            real-world impact.
          </p>
        </motion.div>

        {/* ══ Focus Areas ══ */}
        <motion.div
          className="about-focus-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          viewport={{ once: true }}
        >
          <p className="about-focus-label">Focus Areas</p>
          <div className="about-focus-row">
            {focusAreas.map((area, i) => (
              <motion.div
                key={area.label}
                className="about-focus-card"
                whileHover={{ y: -6, scale: 1.05 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
              >
                <div className="about-focus-icon" style={{ '--icon-color': area.color }}>
                  <area.icon size={22} />
                </div>
                <span className="about-focus-name">{area.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ══ Achievement Cards ══ */}
        <motion.div
          className="about-cards-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          viewport={{ once: true }}
        >

          {/* Desktop: static 3-col grid */}
          <div className="about-cards-desktop">
            {achievements.map((item, i) => {
              const isActive = i === activeIdx;
              return (
                <motion.div
                  key={item.title}
                  className={`about-card ${isActive ? 'about-card--active' : ''}`}
                  onClick={() => setActiveIdx(i)}
                  whileHover={{ scale: 1.02 }}
                  layout
                >
                  <div className="about-card-dot">
                    {isActive && <div className="about-card-dot-ping" />}
                  </div>
                  <div className="about-card-body">
                    <h4 className="about-card-title">{item.title}</h4>
                    <p className="about-card-desc">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile: continuous CSS marquee */}
          <div className="about-marquee-wrap">
            <div className="about-marquee-track">
              {marqueeItems.map((item, i) => (
                <div key={i} className="about-card about-card--marquee">
                  <div className="about-card-body">
                    <h4 className="about-card-title">{item.title}</h4>
                    <p className="about-card-desc">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </motion.div>

        {/* ══ CTA ══ */}
        <motion.div
          className="about-cta-wrap"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          viewport={{ once: true }}
        >
          <button
            className="about-cta-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('navigateToTeam'))}
          >
            <span>Learn More About Us</span>
            <ArrowRight size={18} />
          </button>
        </motion.div>

      </div>
    </section>
  );
});

export default AboutPreview;
